import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, Typography, message, ConfigProvider } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@packages/api-client';
import type { LoginRequest } from '@packages/api-client/src/types';
import logo from '../assets/images/logo.png';

const { Text } = Typography;

interface LoginFormData {
  username: string;
  password: string;
  agree: boolean;
}

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectPath = searchParams.get('redirect') || '/';

  useEffect(() => {
    if (apiClient.isAuthenticated()) {
      navigate(redirectPath, { replace: true });
    }
  }, [navigate, redirectPath]);

  const handleLogin = async (values: LoginFormData) => {
    if (!values.agree) {
      message.warning('请先同意用户隐私政策');
      return;
    }

    setLoading(true);

    try {
      const loginData: LoginRequest = {
        grant_type: 'urn:aialign:grant-type:password',
        username: values.username,
        password: values.password,
        scope: 'api',
      };

      await apiClient.login(loginData);

      message.success('登录成功！');
      navigate(redirectPath, { replace: true });
    } catch (error: any) {
      console.error('登录失败:', error);
      message.error(error.message || '登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginFailed = () => {
    message.error('请检查输入信息');
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1677ff',
          colorPrimaryHover: '#4096ff',
          colorPrimaryActive: '#0958d9',
          borderRadius: 8,
          fontSize: 15,
        },
        components: {
          Input: {
            controlHeightLG: 46,
            colorFillTertiary: '#e8f0fe',
            colorFillSecondary: '#dce8fc',
            activeBg: '#e8f0fe',
            activeBorderColor: '#1677ff',
            inputFontSizeLG: 14,
            paddingInlineLG: 11,
          },
          Button: {
            controlHeightLG: 50,
            contentFontSizeLG: 16,
            fontWeight: 500,
          },
          Form: {
            itemMarginBottom: 16,
            labelFontSize: 14,
            labelColor: '#333',
          },
        },
      }}
    >
      <div className="min-h-screen min-h-[100dvh] flex-center bg-[#f0f2f5] p-4">
        <div className="w-full max-w-md bg-white rounded-xl pt-[36px] px-[28px] pb-[28px] shadow-sm">
          {/* Logo */}
          <img src={logo} alt="AiGN" className="block h-[28px] mb-[28px]" />

          {/* 标题 */}
          <h1 className="mb-1 text-[22px] font-bold text-[#1a1a2e]">用户登录</h1>

          {/* 表单 */}
          <Form
            form={form}
            name="login"
            layout="vertical"
            onFinish={handleLogin}
            onFinishFailed={handleLoginFailed}
            autoComplete="off"
            size="large"
            className="mt-[28px]"
          >
            <Form.Item
              label={<span className="text-sm text-[#333] font-normal">用户名 <span className="text-[#ff4d4f] ml-[2px]">*</span></span>}
              name="username"
              rules={[
                { required: true, message: '请输入用户名' },
                { min: 2, message: '用户名至少2个字符' },
              ]}
            >
              <Input variant="filled" placeholder="请输入用户名" autoComplete="username" />
            </Form.Item>

            <Form.Item
              label={<span className="text-sm text-[#333] font-normal">密码 <span className="text-[#ff4d4f] ml-[2px]">*</span></span>}
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 5, message: '密码至少5个字符' },
              ]}
              className="!mb-1"
            >
              <Input.Password variant="filled" placeholder="请输入密码" autoComplete="current-password" />
            </Form.Item>

            <p className="m-0 text-xs text-[#999] leading-normal">如果忘记密码，请找管理员找回或修改密码</p>

            <Form.Item className="!mt-[28px]">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
              >
                {loading ? '登录中...' : '登录'}
              </Button>
            </Form.Item>

            <Form.Item name="agree" valuePropName="checked" className="!mb-0 mt-2 !text-center">
              <Checkbox className="!text-[13px]">
                <Text className="!text-[13px] !text-[#666]">
                  我已阅读并同意{' '}
                  <span className="text-[#1677ff] cursor-pointer">用户隐私政策</span>
                </Text>
              </Checkbox>
            </Form.Item>
          </Form>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Login;
